/**
 * Project Filtering Hook
 * Custom hook to handle project filtering and transformation logic
 */

import { useMemo } from 'react';
import { projects as allProjects, activities, companies } from '../../../../db';
import { UnifiedProject } from './types';

interface UseProjectFilteringProps {
  activityId?: number;
  companyName?: string;
  skillId?: number;
  requiredSkills?: string[];
  showPrivate: boolean | 'only';
}

export const useProjectFiltering = ({
  activityId,
  companyName,
  skillId,
  requiredSkills,
  showPrivate
}: UseProjectFilteringProps) => {
  
  // Filter and transform projects based on props
  const filteredProjects = useMemo((): UnifiedProject[] => {
    let filtered = allProjects.filter(project => {
      // Activity filter
      if (activityId) {
        return Array.isArray(project.activityId) 
          ? project.activityId.includes(activityId)
          : project.activityId === activityId;
      }
      
      // Company filter
      if (companyName) {
        const activity = project.activityId ? activities.find(a => a.id === project.activityId) : null;
        const company = activity ? companies.find(c => c.id === activity.companyId) : null;
        return company?.name === companyName;
      }
      
      // Skill filter - check categorized skills
      if (requiredSkills && requiredSkills.length > 0) {
        // Check if project has any of the required skills in its categorized skills
        const projectSkills: string[] = [];
        
        // Collect all skill names from all categories
        if (project.categorizedSkills) {
          Object.values(project.categorizedSkills).forEach(skillsArray => {
            skillsArray.forEach(skill => {
              projectSkills.push(skill.name.toLowerCase());
            });
          });
        }
        
        // Also check legacy skills array if it exists
        if ('skills' in project && project.skills) {
          (project.skills as any[]).forEach(skill => {
            if (typeof skill === 'string') {
              projectSkills.push(skill.toLowerCase());
            } else if (skill.name) {
              projectSkills.push(skill.name.toLowerCase());
            }
          });
        }
        
        // Check if any required skill matches project skills
        const hasRequiredSkill = requiredSkills.some(requiredSkill => 
          projectSkills.some(projectSkill => 
            projectSkill.includes(requiredSkill.toLowerCase()) || 
            requiredSkill.toLowerCase().includes(projectSkill)
          )
        );
        
        if (!hasRequiredSkill) {
          return false;
        }
      }
      
      return true;
    });

    // Apply private projects filter
    if (showPrivate === false) {
      // Show only professional projects
      filtered = filtered.filter(project => {
        return Array.isArray(project.activityId) 
          ? project.activityId.length > 0
          : project.activityId !== null;
      });
    } else if (showPrivate === 'only') {
      // Show only private projects
      filtered = filtered.filter(project => {
        return Array.isArray(project.activityId) 
          ? project.activityId.length === 0 || project.activityId.every(id => id === null)
          : project.activityId === null;
      });
    }
    // If showPrivate === true, show all projects (no additional filtering)

    // Transform to UnifiedProject format
    return filtered.map(project => {
      const currentActivityId = Array.isArray(project.activityId) ? project.activityId[0] : project.activityId;
      const currentActivity = currentActivityId ? activities.find(a => a.id === currentActivityId) : null;
      const company = currentActivity ? companies.find(c => c.id === currentActivity.companyId) : null;
      const currentCompanyName = company?.name || '';
      
      // For this initial implementation, we'll consider projects as single-activity
      // Multi-activity support can be added later when the database structure supports it
      const isMultiActivity = false;
      const isContinued = false;

      return {
        ...project,
        currentActivityId,
        currentCompanyName,
        currentActivityTitle: currentActivity?.title,
        isMultiActivity,
        isContinued,
        allActivities: currentActivity ? [{
          id: currentActivity.id,
          title: currentActivity.title,
          companyName: company?.name,
          startDate: currentActivity.startDate,
          endDate: currentActivity.endDate
        }] : []
      } as UnifiedProject;
    });
  }, [activityId, companyName, skillId, requiredSkills, showPrivate]);

  // Sort projects by start date (newest first)
  const sortedProjects = useMemo(() => {
    return [...filteredProjects].sort((a, b) => {
      const dateA = new Date(a.startDate || '1970-01-01').getTime();
      const dateB = new Date(b.startDate || '1970-01-01').getTime();
      return dateB - dateA;
    });
  }, [filteredProjects]);

  const professionalProjects = sortedProjects.filter(p => p.currentActivityId !== null);
  const privateProjects = sortedProjects.filter(p => p.currentActivityId === null);

  return {
    sortedProjects,
    professionalProjects,
    privateProjects,
    totalProjects: sortedProjects.length
  };
};